import { useState } from "react";
import { Check, ChevronDown, SlidersHorizontal } from "lucide-react";
import { groupOptions, matchesFilters } from "../../lib/catalog";

const Swatch = ({ color }) => (
  <span className={`h-5 w-5 shrink-0 rounded-full ring-1 ring-inset ring-black/15 ${color ? "" : "bg-[conic-gradient(#e11d48,#f59e0b,#16a34a,#2563eb,#9333ea,#e11d48)]"}`}
    style={color ? { backgroundColor: color } : undefined} aria-hidden="true" />
);

const OptionButton = ({ group, o, on, count, toggle }) => (
  <li>
    <button type="button" role="checkbox" aria-checked={on} disabled={!on && count === 0} onClick={() => toggle(group, o.id)}
      data-testid={`door-filter-${group}-${o.id}`}
      className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[14px] transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-40 ${on ? "bg-maas-soft font-semibold text-maas-deep" : "text-slate-700 hover:bg-slate-50"}`}>
      {group === "kleur" ? <Swatch color={o.swatch} /> : (
        <span className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] ${on ? "bg-maas text-white" : "ring-1 ring-inset ring-slate-300"}`}>
          {on && <Check className="h-3 w-3" aria-hidden="true" />}
        </span>
      )}
      <span className="flex-1">{o.label}</span>
      <span className="text-[12.5px] tabular-nums text-slate-400">{count}</span>
    </button>
  </li>
);

const FilterGroup = ({ group, doors, filters, toggle, options }) => {
  const present = new Set(doors.flatMap(group.values));
  const list = groupOptions(group.key, options).filter((o) => present.has(o.id));
  if (list.length < 2) return null;
  const pool = doors.filter((d) => matchesFilters(d, filters, group.key));
  return (
    <fieldset data-testid={`door-filter-group-${group.key}`}>
      <legend className="mb-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-slate-500">{group.label}</legend>
      <ul className="space-y-0.5">
        {list.map((o) => (
          <OptionButton key={o.id} group={group.key} o={o} on={(filters[group.key] || []).includes(o.id)} toggle={toggle}
            count={pool.filter((d) => group.values(d).includes(o.id)).length} />
        ))}
      </ul>
    </fieldset>
  );
};

export const DoorFilters = ({ doors, filters, toggle, clear, groups, options }) => {
  const [open, setOpen] = useState(false);
  const active = Object.values(filters).reduce((n, v) => n + v.length, 0);
  return (
    <aside className="lg:col-span-3" aria-label="Filters">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls="door-filters-panel" data-testid="door-filters-toggle"
        className="flex min-h-[52px] w-full items-center justify-between gap-3 rounded-xl border border-slate-300 bg-white px-4 text-[15px] font-semibold text-ink lg:hidden">
        <span className="inline-flex items-center gap-2"><SlidersHorizontal className="h-4 w-4 text-maas" aria-hidden="true" /> Filters
          {active > 0 && <span className="rounded-full bg-maas px-2 py-0.5 text-[12px] text-white" data-testid="door-filters-count">{active}</span>}</span>
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      <div id="door-filters-panel" data-testid="door-filters-panel"
        className={`${open ? "block" : "hidden"} mt-3 space-y-6 rounded-2xl border border-slate-200 bg-white p-5 lg:sticky lg:top-32 lg:mt-0 lg:block`}>
        {groups.map((g) => <FilterGroup key={g.key} group={g} doors={doors} filters={filters} toggle={toggle} options={options} />)}
        <div className="flex gap-2 border-t border-slate-100 pt-4 lg:hidden">
          <button type="button" onClick={clear} className="btn-secondary flex-1 !min-h-[44px] text-[14px]" data-testid="door-filters-reset-mobile">Wissen</button>
          <button type="button" onClick={() => setOpen(false)} className="btn-primary flex-1 !min-h-[44px] text-[14px]" data-testid="door-filters-apply-mobile">Toon deuren</button>
        </div>
      </div>
    </aside>
  );
};
